import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  ConfirmationDialog,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  categorySortIndex,
  getAgencySchedulingPoints,
  getClinicSchedulingPoints,
  previewAgencyBlastRadius,
  updateAgencySchedulingPoints,
  updateClinicSchedulingPoints,
} from '../../services/schedulingPointsApi';
import { useInvalidateSchedulingPointLoads } from '../../hooks/useSchedulingPointLoads';
import type {
  SchedulingPointChange,
  SchedulingPointRowResponse,
  SchedulingPointSource,
  SchedulingPointsGridResponse,
  SchedulingPointsUpdateRequest,
} from '../../services/api/types.gen';
import type { AgencyClinicResponse } from '../../services/clinicsApi';
import { ClinicSelect } from '../../components/clinic/ClinicSelect';
import { useStore } from '../../store';
import {
  getBusinessErrorMessage,
  getValidationErrorItems,
} from '../../utils/apiErrorDetail';
import {
  disciplineName,
  disciplineSortIndex,
} from '../../constants/disciplines';
import { formatDateForDisplay } from '../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

/**
 * Which tier this panel is authoring. Values resolve clinic -> agency ->
 * global; the global tier is Roger's and is not editable from here, so the
 * toggle only ever names the two a manager can speak for.
 */
type Tier = 'clinic' | 'agency';

/** How a tier refers to itself in copy ("… this clinic set"). */
const TIER_POSSESSIVE: Record<Tier, string> = {
  clinic: 'this clinic',
  agency: 'your organization',
};

/** The same words when they open a sentence. */
const TIER_POSSESSIVE_SENTENCE_START: Record<Tier, string> = {
  clinic: 'This clinic',
  agency: 'Your organization',
};

/** What a cleared value at this tier falls back to. */
const TIER_FALLBACK: Record<Tier, string> = {
  clinic: "your organization's standard values",
  agency: "Roger's suggested values",
};

/**
 * The line naming where a row's number came from. A value stated at the tier
 * being edited is *hers*; everything else names its origin so the grid can be
 * read at a glance for how much of it she has actually spoken for.
 */
const sourceLine = (source: SchedulingPointSource, tier: Tier): string => {
  if (source === tier) {
    return tier === 'clinic'
      ? 'Set by this clinic'
      : 'Set by your organization';
  }
  switch (source) {
    case 'agency':
      return "On your organization's standard values";
    case 'global':
      return "On Roger's suggested values";
    case 'unpriced':
      return 'Not yet priced';
    default:
      // A clinic value seen from the agency tier cannot happen (the agency
      // grid is resolved without a clinic), but the union demands a branch.
      return 'Set by a clinic';
  }
};

// ---------------------------------------------------------------------------
// Points formatting
// ---------------------------------------------------------------------------

/** Characters allowed while typing; a lone "." is legal mid-keystroke. */
const POINTS_INPUT_RE = /^\d*\.?\d{0,2}$/;

/**
 * The largest value a points column holds. Mirrors the backend bound, which
 * mirrors the `Numeric(5, 2)` columns underneath it.
 *
 * Checked here as well as there because a save is a batch: one over-range cell
 * comes back as a 422 that refuses every other change in the same request, and
 * the manager has no way to see which cell did it. Caught at the keystroke, the
 * grid can point at the offending row before anything is sent.
 */
const MAX_POINTS = 999.99;

/** Copy for the bound, said the way the field would say it. */
const POINTS_RANGE_SENTENCE = `Enter a number from 0 to ${MAX_POINTS}.`;

/**
 * Whether typed text is actually a number this feature can store.
 *
 * `POINTS_INPUT_RE` deliberately admits a lone "." so the field does not fight
 * the keyboard, which means **every place that commits a value has to reject it
 * again**. A "." that reaches the wire is a 422 that takes the whole batch —
 * every row edit in the same request — down with it. So is 1000.
 */
const isNumericText = (text: string): boolean =>
  POINTS_INPUT_RE.test(text) && /\d/.test(text) && Number(text) <= MAX_POINTS;

/**
 * Display form of a decimal-string points value.
 *
 * `null` is *absence* and renders empty (the "—" placeholder carries it);
 * `"0.00"` is a deliberate zero and renders `0`. These two are never allowed
 * to collapse into the same glyph — absence means we do not measure this,
 * zero means we measured it and it came to nothing.
 */
const formatPoints = (points: string | null): string => {
  if (points === null) return '';
  const value = Number(points);
  if (!Number.isFinite(value)) return points;
  // Trim the storage precision so the editable value reads as a number a
  // person would say out loud: 2, 1.5, 0.
  return String(value);
};

type PointsState = 'valued' | 'zero' | 'unpriced';

const pointsState = (row: SchedulingPointRowResponse): PointsState => {
  if (row.source === 'unpriced' || row.points === null) return 'unpriced';
  return Number(row.points) === 0 ? 'zero' : 'valued';
};

// ---------------------------------------------------------------------------
// Edit state — the tri-state that makes this screen safe
// ---------------------------------------------------------------------------

/**
 * A row's local edits. **A key that is absent means untouched**, and that is
 * the whole safety property of this screen: the payload builder omits absent
 * keys, and the backend reads an omitted key as "leave this alone" but a
 * present-and-null key as an explicit tombstone. A builder that defaulted to
 * `null` would clear every value the manager never looked at.
 *
 * `points` and `note` hold raw input text so an in-progress edit round-trips
 * without coercion; `''` is the *clear* gesture, not an empty value. `enabled`
 * has no empty string to spare, so its clear gesture is `null` — a pending
 * reset to whatever the tier below says.
 */
type RowEdits = {
  points?: string;
  enabled?: boolean | null;
  note?: string;
};

type EditMap = Record<string, RowEdits>;

const rowKey = (row: { task_type: string; type_key: string }): string =>
  `${row.task_type}:${row.type_key}`;

/** Which fields of a row will actually be sent. Drives badges and highlights. */
type ChangedField = 'points' | 'enabled' | 'note';

/**
 * What one cell contributes to the payload. The four arms are the whole
 * contract with the backend: `omit` writes no key at all, `clear` writes the
 * key with `null` (a tombstone), `set` writes a value, `invalid` refuses.
 */
type CellPlan<T> =
  | { kind: 'omit' }
  | { kind: 'set'; value: T }
  | { kind: 'clear' }
  | { kind: 'invalid' };

const OMIT = { kind: 'omit' } as const;

/**
 * Decide what a points edit means.
 *
 * - untouched -> omit
 * - emptied -> clear, **but only if this tier is the one that stated the
 *   value**. A tier that says nothing has no tombstone to write, so clearing
 *   an inherited row is a no-op rather than a junk append.
 * - re-typing this tier's own number -> omit (nothing changed)
 * - typing over an inherited number -> set, even when the digits match: that
 *   is a deliberate act of adoption, and the row becomes hers.
 */
const planPoints = (
  row: SchedulingPointRowResponse,
  edit: string | undefined,
  tier: Tier
): CellPlan<string> => {
  if (edit === undefined) return OMIT;
  const text = edit.trim();
  if (text === '') {
    return row.source === tier ? { kind: 'clear' } : OMIT;
  }
  if (!isNumericText(text)) {
    return { kind: 'invalid' };
  }
  const next = Number(text);
  if (!Number.isFinite(next)) return { kind: 'invalid' };
  if (
    row.source === tier &&
    row.points !== null &&
    Number(row.points) === next
  ) {
    return OMIT;
  }
  return { kind: 'set', value: text };
};

/**
 * Decide what a note edit means.
 *
 * A note resolves down the tier chain independently of the number, so the note
 * on display is not necessarily written by whoever supplied the points:
 * `note_source` is the only field that says whose words these are. Clearing is
 * gated on *that* — a clinic may retract its own comparison on a row whose
 * number it inherits, and may not delete the agency's words on a row it prices
 * itself.
 */
const planNote = (
  row: SchedulingPointRowResponse,
  edit: string | undefined,
  tier: Tier
): CellPlan<string> => {
  if (edit === undefined) return OMIT;
  const text = edit.trim();
  const current = (row.note ?? '').trim();
  if (text === current) return OMIT;
  if (text === '') return row.note_source === tier ? { kind: 'clear' } : OMIT;
  return { kind: 'set', value: text };
};

/**
 * Decide what a Counted toggle means.
 *
 * `null` is the reset gesture, and it has to write a *tombstone* rather than
 * the inherited value: saving `enabled: false` because the tier below says
 * false looks identical on screen and is a different act entirely — it freezes
 * this row against every future change the tier below makes. Gated on
 * `enabled_source` for the reason `planNote` is gated on `note_source`: a tier
 * that never spoke has no tombstone to write.
 *
 * A typed toggle that matches the resolved value is a no-op, even on an
 * inherited row: nothing is changing, so nothing is sent.
 */
const planEnabled = (
  row: SchedulingPointRowResponse,
  edit: boolean | null | undefined,
  tier: Tier
): CellPlan<boolean> => {
  if (edit === undefined) return OMIT;
  if (edit === null) {
    return row.enabled_source === tier ? { kind: 'clear' } : OMIT;
  }
  return edit === row.enabled ? OMIT : { kind: 'set', value: edit };
};

type ChangePlan = {
  changes: SchedulingPointChange[];
  /**
   * The row each entry of `changes` came from, same order.
   *
   * A 422 names its offender positionally (`body.changes.3.points`), and that
   * index means nothing on screen. This is how the answer gets back to a row.
   */
  changeRowKeys: string[];
  changedByRow: Map<string, ChangedField[]>;
  invalidRowKeys: string[];
};

/**
 * Build the PUT body's `changes` array from the loaded grid plus local edits.
 *
 * Every key is *assigned* only when it is genuinely changing; untouched keys
 * are never written onto the object at all, so they do not appear in the JSON.
 */
const buildChanges = (
  rows: SchedulingPointRowResponse[],
  edits: EditMap,
  tier: Tier
): ChangePlan => {
  const changes: SchedulingPointChange[] = [];
  const changeRowKeys: string[] = [];
  const changedByRow = new Map<string, ChangedField[]>();
  const invalidRowKeys: string[] = [];

  for (const row of rows) {
    const key = rowKey(row);
    const edit = edits[key];
    if (!edit) continue;

    const points = planPoints(row, edit.points, tier);
    if (points.kind === 'invalid') {
      invalidRowKeys.push(key);
      continue;
    }
    const enabled = planEnabled(row, edit.enabled, tier);
    const note = planNote(row, edit.note, tier);

    const change: SchedulingPointChange = {
      task_type: row.task_type,
      type_key: row.type_key,
    };
    const fields: ChangedField[] = [];

    if (points.kind === 'set') {
      change.points = points.value;
      fields.push('points');
    } else if (points.kind === 'clear') {
      change.points = null;
      fields.push('points');
    }
    if (enabled.kind === 'set') {
      change.enabled = enabled.value;
      fields.push('enabled');
    } else if (enabled.kind === 'clear') {
      change.enabled = null;
      fields.push('enabled');
    }
    if (note.kind === 'set') {
      change.note = note.value;
      fields.push('note');
    } else if (note.kind === 'clear') {
      change.note = null;
      fields.push('note');
    }

    if (fields.length > 0) {
      changes.push(change);
      changeRowKeys.push(key);
      changedByRow.set(key, fields);
    }
  }

  return { changes, changeRowKeys, changedByRow, invalidRowKeys };
};

/**
 * Which rows a rejected save was actually about.
 *
 * FastAPI names the offending field by position — `body.changes.3.points` — so
 * the index has to be read back through the same array the payload was built
 * from. Anything the response points at that is not a row (a config field, a
 * complaint about the body itself) yields nothing, and the caller falls back to
 * generic copy rather than highlighting a cell at random.
 */
const rejectedRowsFromError = (
  error: unknown,
  changeRowKeys: string[]
): string[] => {
  const keys = new Set<string>();
  for (const item of getValidationErrorItems(error)) {
    const at = item.loc.indexOf('changes');
    if (at === -1) continue;
    const index = item.loc[at + 1];
    if (typeof index !== 'number') continue;
    const key = changeRowKeys[index];
    if (key) keys.add(key);
  }
  return [...keys];
};

type ConfigEdits = {
  unitLabel?: string;
  travel?: string;
};

type ConfigPatch = Pick<
  SchedulingPointsUpdateRequest,
  'unit_label' | 'missed_visit_travel_points'
>;

type ConfigPlan = {
  /** What the config half of the PUT body carries; `null` sends nothing. */
  patch: ConfigPatch | null;
  /** The travel field holds something that is not a number. */
  travelInvalid: boolean;
};

const NO_CONFIG_CHANGE: ConfigPlan = { patch: null, travelInvalid: false };

/**
 * Build the config half of the PUT body.
 *
 * Only the touched field travels. The backend honours the field mask all the
 * way into the SQL, so naming the untouched one would re-assert a value read
 * before the save — and two admins editing the label and the travel value in
 * the same minute would each revert the other. Omitting it is what makes the
 * two edits merge.
 *
 * Clearing either field follows the same rule as clearing a row: a tier that
 * never stated the value has no tombstone to write, so the clear is a no-op
 * rather than a junk append.
 */
const buildConfigPatch = (
  grid: SchedulingPointsGridResponse,
  config: ConfigEdits,
  tier: Tier
): ConfigPlan => {
  const labelText = config.unitLabel?.trim();
  const travelText = config.travel?.trim();

  if (
    travelText !== undefined &&
    travelText !== '' &&
    !isNumericText(travelText)
  ) {
    // Nothing goes out while the field is unreadable — not even the row
    // changes, which the save gate stops alongside it.
    return { patch: null, travelInvalid: true };
  }

  const labelChanged =
    labelText !== undefined &&
    (labelText === ''
      ? grid.unit_label_source === tier
      : labelText !== grid.unit_label || grid.unit_label_source !== tier);
  const travelChanged =
    travelText !== undefined &&
    (travelText === ''
      ? grid.missed_visit_travel_source === tier
      : Number(travelText) !== Number(grid.missed_visit_travel_points) ||
        grid.missed_visit_travel_source !== tier);

  if (!labelChanged && !travelChanged) return NO_CONFIG_CHANGE;

  const patch: ConfigPatch = {};
  if (labelChanged) patch.unit_label = labelText || null;
  if (travelChanged) patch.missed_visit_travel_points = travelText || null;

  return { patch, travelInvalid: false };
};

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

type DisciplineGroup = {
  groupKey: string;
  disciplineLabel: string;
  rows: SchedulingPointRowResponse[];
};

type CategoryGroup = {
  category: string;
  label: string;
  rows: SchedulingPointRowResponse[];
  groups: DisciplineGroup[];
};

/**
 * What one group is called, everywhere it is named.
 *
 * The confirmation dialog and the buttons' accessible names say the same thing,
 * so they say it in the same words — a screen-reader user who acts on "Set all
 * Skilled Nursing · Routine visits" must hear the dialog name the same group
 * back, not a near-miss.
 */
const groupDisplayName = (
  group: DisciplineGroup,
  categoryLabel: string
): string => `${group.disciplineLabel} · ${categoryLabel}`;

/**
 * Accordion by category, and inside it one table per discipline. The nesting
 * is not decoration: `group_key` is `discipline:category` precisely so a
 * manager can act on "All Nursing OASIS" at once without those twenty rows
 * collapsing into one.
 */
const groupRows = (rows: SchedulingPointRowResponse[]): CategoryGroup[] => {
  const byCategory = new Map<string, SchedulingPointRowResponse[]>();
  for (const row of rows) {
    const existing = byCategory.get(row.category);
    if (existing) existing.push(row);
    else byCategory.set(row.category, [row]);
  }

  return [...byCategory.entries()]
    .sort(
      ([a], [b]) =>
        categorySortIndex(a as SchedulingPointRowResponse['category']) -
        categorySortIndex(b as SchedulingPointRowResponse['category'])
    )
    .map(([category, categoryRows]) => {
      const byGroup = new Map<string, SchedulingPointRowResponse[]>();
      for (const row of categoryRows) {
        const existing = byGroup.get(row.group_key);
        if (existing) existing.push(row);
        else byGroup.set(row.group_key, [row]);
      }
      const groups = [...byGroup.entries()]
        .sort(([, a], [, b]) => {
          const left = a[0].discipline ?? '';
          const right = b[0].discipline ?? '';
          return (
            disciplineSortIndex(left) - disciplineSortIndex(right) ||
            left.localeCompare(right)
          );
        })
        .map(([groupKey, groupMembers]) => ({
          groupKey,
          disciplineLabel: groupMembers[0].discipline
            ? disciplineName(groupMembers[0].discipline)
            : 'All disciplines',
          rows: groupMembers,
        }));
      return {
        category,
        label: categoryRows[0].category_label,
        rows: categoryRows,
        groups,
      };
    });
};

// ---------------------------------------------------------------------------
// What a row says *now* — server value plus whatever has been typed over it
// ---------------------------------------------------------------------------

/**
 * A row as the manager currently sees it.
 *
 * The dialogs describe what an action will destroy, and the thing at risk is
 * what is on screen, not what the server last sent. Naming the saved 3 while
 * overwriting the typed 5 is the one way a confirmation can lie.
 */
type StatedRow = {
  row: SchedulingPointRowResponse;
  /** The value shown in the cell right now. */
  text: string;
  /** Whether this tier owns that value — after saving, if edits are pending. */
  isOwn: boolean;
  /** Whether this tier owns the note shown right now. */
  ownsNote: boolean;
  /** Whether this tier owns the Counted choice shown right now. */
  ownsEnabled: boolean;
};

/**
 * Resolve a row against its pending edit.
 *
 * A typed value is *hers* the moment it is typed, even on a row she inherits:
 * saving makes it so, and a dialog that called it inherited would be describing
 * a state that no longer exists. An emptied cell is the reverse — nothing of
 * hers is left to destroy.
 */
const stateRow = (
  row: SchedulingPointRowResponse,
  edit: RowEdits | undefined,
  tier: Tier
): StatedRow => {
  const typed = edit?.points;
  const text = typed ?? formatPoints(row.points);
  const isOwn =
    typed !== undefined
      ? typed.trim() !== ''
      : row.source === tier && row.points !== null;
  const typedNote = edit?.note;
  const ownsNote =
    typedNote !== undefined
      ? typedNote.trim() !== ''
      : row.note_source === tier && !!row.note;
  // A typed toggle is hers the moment it is typed, same doctrine as the
  // number; a pending reset is the moment it stops being.
  const typedEnabled = edit?.enabled;
  const ownsEnabled =
    typedEnabled !== undefined
      ? typedEnabled !== null
      : row.enabled_source === tier;
  return { row, text, isOwn, ownsNote, ownsEnabled };
};

/** Names of the rows a bulk action would overwrite, capped so copy stays readable. */
const NAME_LIST_CAP = 6;

const listNames = (stated: StatedRow[]): string => {
  const shown = stated
    .slice(0, NAME_LIST_CAP)
    .map(({ row, text }) => `${row.display_name} (${text})`)
    .join(', ');
  const rest = stated.length - NAME_LIST_CAP;
  return rest > 0 ? `${shown}, and ${rest} more` : shown;
};

/** The form of a word that agrees with a count: "1 value is", "2 values are". */
const agree = (count: number, singular: string, pluralForm: string): string =>
  count === 1 ? singular : pluralForm;

const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${agree(count, singular, pluralForm)}`;

// ---------------------------------------------------------------------------
// Confirmation plumbing
// ---------------------------------------------------------------------------

/**
 * A save, held as one thing: the body that goes out and the row keys that read
 * its positions back. A 422 is answered with `changeRowKeys[index]`, so the two
 * are only ever correct together — separated, the highlight can name rows from
 * a body that was never sent.
 */
type SaveAttempt = {
  body: SchedulingPointsUpdateRequest;
  changeRowKeys: string[];
};

/**
 * One confirmation at a time, carrying its own accept closure. Every
 * destructive act on this screen states what it will destroy *before* it
 * happens — undo afterwards is consolation, undo before is trust.
 */
type ConfirmSpec = {
  title: string;
  message: string;
  confirmLabel: string;
  onAccept: () => void;
};

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

type SchedulingPointsSectionProps = {
  clinics: AgencyClinicResponse[];
  /**
   * Whether the parent is still fetching the clinic list.
   *
   * A bare array cannot distinguish "none yet" from "none exist", and the
   * empty state here is an *instruction* — telling a manager to create a
   * clinic she already has invites a duplicate. Absent loading state, the
   * instruction is what renders for the whole fetch.
   */
  isLoadingClinics?: boolean;
};

/**
 * Scheduling Points admin surface (EMR-294).
 *
 * Its own full-width section rather than a tab inside the insurance modal:
 * two grids of decimals about visits, one of which is money, is exactly the
 * adjacency that makes a manager hesitate over whether she is setting pay.
 */
const SchedulingPointsSection: React.FC<SchedulingPointsSectionProps> = ({
  clinics,
  isLoadingClinics = false,
}) => {
  const { canEdit } = useAdminSectionAccess('scheduling-points');
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [tier, setTier] = useState<Tier>('clinic');
  const [clinicId, setClinicId] = useState('');
  const [includeAllDisciplines, setIncludeAllDisciplines] = useState(false);

  const [grid, setGrid] = useState<SchedulingPointsGridResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // A saved value reprices every cached clinician-workload week, so the badge
  // caches are dropped on save success — the admin who just repriced may go
  // straight to a patient to see the effect, and a number that ignores what
  // you just did is worse than no number at all.
  const invalidateSchedulingPointLoads = useInvalidateSchedulingPointLoads();
  const [loadFailed, setLoadFailed] = useState(false);
  // Set when a save round-trip failed. The edits stay exactly where they were;
  // this only changes how they *look*, so nothing is silently discarded.
  const [saveFailed, setSaveFailed] = useState(false);
  // Rows the server itself refused, by row key. The client validates the same
  // bounds, so this should stay empty — but when the two disagree the manager
  // needs the disagreement pointed at, not a generic apology.
  const [rejectedRowKeys, setRejectedRowKeys] = useState<string[]>([]);

  const [edits, setEdits] = useState<EditMap>({});
  const [configEdits, setConfigEdits] = useState<ConfigEdits>({});
  const [bulkText, setBulkText] = useState<Record<string, string>>({});
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [confirmation, setConfirmation] = useState<ConfirmSpec | null>(null);

  // Identifies the currently loaded grid; a response from a superseded tier or
  // clinic must never land on the grid that replaced it.
  const sessionRef = useRef(0);

  /**
   * Where the keyboard was standing when the freeze took hold.
   *
   * Every control that starts a save or opens a dialog disables in the same
   * commit, and a disabled button cannot hold focus — so by the time the dialog
   * is up, focus has already fallen to `document.body`, and that is what the
   * dialog records as the place to give it back to. A manager working by
   * keyboard or screen reader would lose her seat on every save. This is the
   * seat, remembered on the way in.
   */
  const freezeFocusRef = useRef<HTMLElement | null>(null);

  const captureFreezeFocus = () => {
    const active = document.activeElement;
    freezeFocusRef.current = active instanceof HTMLElement ? active : null;
  };

  useEffect(() => {
    if (!clinicId && clinics.length > 0) setClinicId(clinics[0].id);
  }, [clinics, clinicId]);

  const loadGrid = async () => {
    if (tier === 'clinic' && !clinicId) return;
    const session = sessionRef.current + 1;
    sessionRef.current = session;
    setIsLoading(true);
    setLoadFailed(false);
    setSaveFailed(false);
    setRejectedRowKeys([]);
    try {
      const { data } =
        tier === 'agency'
          ? await getAgencySchedulingPoints({ throwOnError: true })
          : await getClinicSchedulingPoints({
              path: { clinic_id: clinicId },
              query: { include_all_disciplines: includeAllDisciplines },
              throwOnError: true,
            });
      if (session !== sessionRef.current) return;
      applyGrid(data);
    } catch (error) {
      if (session !== sessionRef.current) return;
      console.error('Failed to load scheduling points:', error);
      setLoadFailed(true);
      showError(
        'Failed to load scheduling points',
        getBusinessErrorMessage(error) ??
          'Unable to load data. Please try again.'
      );
    } finally {
      if (session === sessionRef.current) setIsLoading(false);
    }
  };

  const applyGrid = (
    data: SchedulingPointsGridResponse,
    { keepOpen = false }: { keepOpen?: boolean } = {}
  ) => {
    setGrid(data);
    setEdits({});
    setConfigEdits({});
    setBulkText({});
    setSaveFailed(false);
    setRejectedRowKeys([]);
    if (!keepOpen) {
      // A fresh grid opens on its first category; a save leaves the manager
      // exactly where she was working.
      const categories = groupRows(data.rows);
      setOpenCategories(categories.length > 0 ? [categories[0].category] : []);
    }
  };

  /**
   * What a load actually depends on, per tier.
   *
   * The agency grid is resolved without a clinic, so a clinic id changing
   * underneath it means nothing — and it does change underneath it. The
   * clinics-arrival effect above is state-driven, not a control: no `disabled`
   * gate can reach it, so the freeze that holds every other write shut has no
   * say over this one. Keyed on the tier's own inputs, that write stays the
   * harmless thing it is meant to be — a pre-selection for a later switch to
   * the clinic tier — instead of a reload that replaces the grid mid-edit and
   * bumps the session out from under a save already in flight.
   */
  const loadKey =
    tier === 'agency'
      ? 'agency'
      : `clinic:${clinicId}:${includeAllDisciplines}`;

  useEffect(() => {
    void loadGrid();
  }, [loadKey]);

  const rows = grid?.rows ?? [];
  const categories = useMemo(() => groupRows(rows), [rows]);

  const plan = useMemo(
    () => buildChanges(rows, edits, tier),
    [rows, edits, tier]
  );
  const configPlan = useMemo(
    () => (grid ? buildConfigPatch(grid, configEdits, tier) : NO_CONFIG_CHANGE),
    [grid, configEdits, tier]
  );
  const configPatch = configPlan.patch;
  const travelInvalid = configPlan.travelInvalid;
  // What the row highlighting and the save gate both read: cells this screen
  // refuses to send, plus cells the server sent back. The two are the same kind
  // of problem to the manager — a value to fix before anything can save.
  const invalidRowKeys = useMemo(
    () => [...new Set([...plan.invalidRowKeys, ...rejectedRowKeys])],
    [plan.invalidRowKeys, rejectedRowKeys]
  );
  const pendingCells = [...plan.changedByRow.values()].reduce(
    (total, fields) => total + fields.length,
    0
  );
  const pendingCount = pendingCells + (configPatch ? 1 : 0);
  // Counted separately from pendingCount: an invalid cell is not a pending
  // change (it will never be sent), but it is also not "nothing" — the footer
  // must not read "No unsaved changes" while a cell sits red.
  const invalidCount = invalidRowKeys.length + (travelInvalid ? 1 : 0);
  const isDirty =
    pendingCount > 0 ||
    invalidRowKeys.length > 0 ||
    travelInvalid ||
    Object.keys(edits).length > 0;

  // How much of this grid the manager has actually spoken for. Rendered as a
  // single line so the answer is available without reading every row.
  const provenanceCounts = useMemo(() => {
    const counts = { own: 0, agency: 0, global: 0, unpriced: 0 };
    for (const row of rows) {
      if (row.source === tier) counts.own += 1;
      else if (row.source === 'agency') counts.agency += 1;
      else if (row.source === 'global') counts.global += 1;
      else counts.unpriced += 1;
    }
    return counts;
  }, [rows, tier]);

  const unitLabel =
    configEdits.unitLabel !== undefined
      ? configEdits.unitLabel
      : (grid?.unit_label ?? 'points');
  const unitLabelForCopy = unitLabel.trim() || 'points';
  const valueColumnLabel =
    unitLabelForCopy.charAt(0).toUpperCase() + unitLabelForCopy.slice(1);

  const updateRow = (key: string, patch: RowEdits) => {
    setSaveFailed(false);
    // Only this row's rejection is lifted. Clearing them all would take the
    // highlight off the rows she has not looked at yet.
    setRejectedRowKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : prev
    );
    setEdits(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const discardEdits = () => {
    setEdits({});
    setConfigEdits({});
    setBulkText({});
    setSaveFailed(false);
    setRejectedRowKeys([]);
  };

  /**
   * The footer's Discard destroys exactly what a tier switch destroys, and it
   * stands next to Save. Doing that on one mis-click, silently, is the single
   * place this screen would break its own rule — so it asks first, in the same
   * words the switch guard uses, minus the going-somewhere.
   */
  const requestDiscardEdits = () => {
    captureFreezeFocus();
    setConfirmation({
      title: 'Discard unsaved changes?',
      message:
        pendingCount > 0
          ? `You have ${plural(pendingCount, 'unsaved change', 'unsaved changes')} on this grid. Discarding clears ${agree(pendingCount, 'it', 'them')}.`
          : 'Your edits on this grid would not save any changes. Discarding clears them.',
      confirmLabel: 'Discard changes',
      onAccept: () => {
        setConfirmation(null);
        discardEdits();
      },
    });
  };

  /** Switching tier or clinic is a navigation away from unsaved work. */
  const guardSwitch = (apply: () => void) => {
    if (!isDirty) {
      apply();
      return;
    }
    captureFreezeFocus();
    setConfirmation({
      title: 'Discard unsaved changes?',
      // Dirty is not the same as sendable: emptying an inherited row is a
      // legitimate no-op, and this screen does not get to say "0 unsaved
      // changes" on the one dialog whose whole job is naming what it destroys.
      message:
        pendingCount > 0
          ? `You have ${plural(pendingCount, 'unsaved change', 'unsaved changes')} on this grid. Leaving discards ${agree(pendingCount, 'it', 'them')}.`
          : 'Your edits on this grid would not save any changes. Leaving clears them.',
      confirmLabel: 'Discard changes',
      onAccept: () => {
        setConfirmation(null);
        discardEdits();
        apply();
      },
    });
  };

  // -------------------------------------------------------------------------
  // Bulk actions — each states what it destroys before it happens
  // -------------------------------------------------------------------------

  const requestBulkSet = (group: DisciplineGroup, categoryLabel: string) => {
    const raw = (bulkText[group.groupKey] ?? '').trim();
    if (raw === '') {
      showError(
        'Enter a value first',
        `Type the ${unitLabelForCopy} to apply to every row in this group.`
      );
      return;
    }
    if (!isNumericText(raw)) {
      showError('Check this value', POINTS_RANGE_SENTENCE);
      return;
    }
    // Read against the pending edits, not the loaded grid: a value typed a
    // moment ago is as real to the manager as one the server sent, and it is
    // just as thoroughly overwritten by this.
    const stated = group.rows.map(row =>
      stateRow(row, edits[rowKey(row)], tier)
    );
    const owned = stated.filter(entry => entry.isOwn);
    const groupLabel = groupDisplayName(group, categoryLabel);
    const inherited = group.rows.length - owned.length;

    captureFreezeFocus();
    setConfirmation({
      title: `Set all ${groupLabel} to ${raw}?`,
      message:
        owned.length === 0
          ? `${plural(group.rows.length, 'row', 'rows')} will be set to ${raw} ${unitLabelForCopy}. None of them carry a value ${TIER_POSSESSIVE[tier]} set, so nothing is replaced.`
          : `${plural(owned.length, 'value', 'values')} ${TIER_POSSESSIVE[tier]} set will be replaced: ${listNames(owned)}.` +
            (inherited > 0
              ? ` ${plural(inherited, 'inherited row', 'inherited rows')} will also be set to ${raw}.`
              : ''),
      confirmLabel:
        owned.length === 0
          ? `Set ${plural(group.rows.length, 'value', 'values')}`
          : `Replace ${plural(owned.length, 'value', 'values')}`,
      onAccept: () => {
        setConfirmation(null);
        setSaveFailed(false);
        const keys = group.rows.map(rowKey);
        setRejectedRowKeys(prev => prev.filter(key => !keys.includes(key)));
        setEdits(prev => {
          const next = { ...prev };
          for (const key of keys) {
            next[key] = { ...next[key], points: raw };
          }
          return next;
        });
        setBulkText(prev => ({ ...prev, [group.groupKey]: '' }));
      },
    });
  };

  /**
   * Reset-to-suggested lives here, per group, and deliberately has no
   * top-level counterpart. Overwriting a clinic's deliberated judgment about
   * its social worker's job with a stranger's table is not the same act as
   * resetting a billing code, and it should not be one button away.
   */
  const requestGroupReset = (group: DisciplineGroup, categoryLabel: string) => {
    // Pending edits count as this tier's values here too. A manager who types
    // five numbers and then resets the group means all five, and a reset that
    // left them standing would look like it had done nothing.
    const stated = group.rows.map(row =>
      stateRow(row, edits[rowKey(row)], tier)
    );
    const owned = stated.filter(entry => entry.isOwn);
    // A Counted choice is an override like any other, and a group whose only
    // override is a switch still has something to give back.
    const switched = stated.filter(entry => entry.ownsEnabled).length;
    if (owned.length === 0 && switched === 0) return;
    const groupLabel = groupDisplayName(group, categoryLabel);
    // A note is a justification *for a number*. Giving the number back and
    // keeping the justification leaves her reasoning standing beside someone
    // else's value, with no row of her own left to edit it from.
    const noted = stated.filter(entry => entry.ownsNote).length;

    // One sentence per kind of thing being given back, and none for a kind
    // there is none of — "0 values will be cleared: " is the sort of sentence
    // that makes a manager distrust the rest of the dialog.
    const sentences = [
      owned.length > 0
        ? `${plural(owned.length, 'value', 'values')} ${TIER_POSSESSIVE[tier]} set will be cleared: ${listNames(owned)}. Those rows fall back to ${TIER_FALLBACK[tier]}.`
        : '',
      switched > 0
        ? `${plural(switched, 'Counted switch', 'Counted switches')} ${TIER_POSSESSIVE[tier]} set ${agree(switched, 'returns', 'return')} to the default.`
        : '',
      noted > 0
        ? `${plural(noted, 'comparison note', 'comparison notes')} ${TIER_POSSESSIVE[tier]} wrote will be cleared as well.`
        : '',
    ].filter(Boolean);

    captureFreezeFocus();
    setConfirmation({
      title: `Reset ${groupLabel} to suggested?`,
      message: sentences.join(' '),
      confirmLabel:
        owned.length > 0
          ? `Clear ${plural(owned.length, 'value', 'values')}`
          : `Clear ${plural(switched, 'Counted switch', 'Counted switches')}`,
      onAccept: () => {
        setConfirmation(null);
        setSaveFailed(false);
        const keys = group.rows.map(rowKey);
        setRejectedRowKeys(prev => prev.filter(key => !keys.includes(key)));
        setEdits(prev => {
          const next = { ...prev };
          for (const row of group.rows) {
            const key = rowKey(row);
            const patch: RowEdits = { ...next[key] };
            // Two different acts, and the row decides which one applies.
            // Where this tier states the value, clearing writes a tombstone;
            // where a typed value is all that made the row hers, dropping the
            // edit is the whole reset — a tombstone there would be a junk
            // append against a row she never spoke for.
            if (row.source === tier) patch.points = '';
            else delete patch.points;
            if (row.note_source === tier) patch.note = '';
            else delete patch.note;
            // Same two acts for the switch. `null` is the tombstone; dropping
            // the edit is how a toggle that was never saved is taken back.
            if (row.enabled_source === tier) patch.enabled = null;
            else delete patch.enabled;
            // An entry with nothing left in it is not an edit; keeping it
            // would leave the grid looking dirty with nothing to discard.
            if (Object.keys(patch).length === 0) delete next[key];
            else next[key] = patch;
          }
          return next;
        });
      },
    });
  };

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------

  /**
   * The body a save would send, built once and used twice.
   *
   * The blast-radius preview asks about this exact object, so the dialog and
   * the write cannot come to disagree about what is changing. Only the keys the
   * config patch actually carries are assigned: an assigned `undefined` still
   * answers `in`, and omit-vs-null is read that way.
   */
  const buildBody = (): SchedulingPointsUpdateRequest => {
    const body: SchedulingPointsUpdateRequest = { changes: plan.changes };
    if (configPatch) Object.assign(body, configPatch);
    return body;
  };

  // `prebuilt` is the body the blast-radius dialog counted, carried back by the
  // accept closure so the write is the counted object itself, not a rebuild
  // from whatever the screen holds at accept time. The grid is frozen for that
  // window too, but identity beats discipline — a control that forgets the
  // freeze cannot change what this saves.
  //
  // `changeRowKeys` travels *with* the body rather than being read from the
  // closure, because it is the body's own index: a 422 names its offender by
  // position in `changes`, and the only array that can name it back is the one
  // that array was built from. Pairing them makes that a fact of the type
  // rather than a coincidence of both being read in the same render.
  const sendSave = async (prebuilt?: SaveAttempt) => {
    if (!grid) return;
    const body = prebuilt?.body ?? buildBody();

    // Same invariant as `loadGrid`, and it matters more here: a PUT that
    // resolves after the grid was replaced would repaint the old clinic's rows
    // under the new clinic's id, and the next edit would diff against that
    // borrowed baseline and write it into the wrong tenant.
    const session = sessionRef.current;
    setIsSaving(true);
    try {
      const { data } =
        tier === 'agency'
          ? await updateAgencySchedulingPoints({ body, throwOnError: true })
          : await updateClinicSchedulingPoints({
              path: { clinic_id: clinicId },
              // The save must ask the same question the load asked. The PUT
              // returns a rebuilt grid that replaces this one wholesale, so
              // narrowing it differently would make a row the manager just
              // edited vanish behind a success toast — indistinguishable from
              // the save having thrown her value away.
              query: { include_all_disciplines: includeAllDisciplines },
              body,
              throwOnError: true,
            });
      // Before the session guard on purpose: the PUT landed server-side, so
      // the cached workload weeks are stale even if this panel has since
      // moved to another clinic or unmounted mid-flight.
      void invalidateSchedulingPointLoads();
      if (session !== sessionRef.current) return;
      applyGrid(data, { keepOpen: true });
      showSuccess(
        'Scheduling points saved',
        tier === 'agency'
          ? "Your organization's standard values are updated."
          : 'This clinic now states these values.'
      );
    } catch (error) {
      // Nothing is cleared. Forty minutes of moral arithmetic is not an
      // ordinary form error, and it does not get thrown away on our behalf.
      console.error('Failed to save scheduling points:', error);
      if (session !== sessionRef.current) return;
      setSaveFailed(true);
      // A rejected batch says which cell it choked on, and the answer is worth
      // carrying back to the grid: without it the manager is told to fix
      // something and not told what, on a screen with two hundred cells.
      const rejected = rejectedRowsFromError(
        error,
        prebuilt?.changeRowKeys ?? plan.changeRowKeys
      );
      if (rejected.length > 0) {
        setRejectedRowKeys(rejected);
        showError(
          'Failed to save scheduling points',
          `Fix the highlighted ${agree(rejected.length, 'value', 'values')}. ${POINTS_RANGE_SENTENCE}`
        );
      } else {
        showError(
          'Failed to save scheduling points',
          getBusinessErrorMessage(error) ?? 'Unable to save. Please try again.'
        );
      }
    } finally {
      // Not session-guarded: this flag belongs to the button, not the grid, and
      // leaving it set would disable saving for good.
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    // Read before anything disables: the Save button is about to become
    // unfocusable, and this is the only moment it can still say where it was.
    captureFreezeFocus();
    // The travel field is gated with the rows, not after them: one unreadable
    // config value 422s the request, and the rows in the same body die with it.
    if (invalidCount > 0) {
      showError(
        'Check the highlighted values',
        `Fix the highlighted ${agree(invalidCount, 'value', 'values')}. ${POINTS_RANGE_SENTENCE}`
      );
      return;
    }
    if (pendingCount === 0) return;

    if (tier === 'clinic') {
      await sendSave();
      return;
    }

    // An agency edit's risk is accidental breadth, so the guard is
    // information: say how many clinics actually move before she commits.
    let message =
      "We couldn't count how many clinics this affects. It changes the default for every clinic that has no values of its own.";
    // Built here, counted here, and carried into the accept closure below:
    // one object from question to write, and the row keys that read its
    // positions back travelling with it.
    const attempt: SaveAttempt = {
      body: buildBody(),
      changeRowKeys: plan.changeRowKeys,
    };
    // Held through the count as well as the write, so a second click cannot
    // fire a second blast-radius request behind the first — and so the grid
    // stays frozen from the question to the answer.
    setIsSaving(true);
    try {
      const { data } = await previewAgencyBlastRadius({
        body: attempt.body,
        throwOnError: true,
      });
      message = `This changes the default for ${plural(data.inheriting_clinics, 'clinic', 'clinics')}. ${plural(data.overriding_clinics, 'clinic has', 'clinics have')} their own values and won't be affected.`;
    } catch (error) {
      console.error('Failed to load agency blast radius:', error);
    } finally {
      setIsSaving(false);
    }

    setConfirmation({
      title: "Change your organization's standard values?",
      message,
      confirmLabel: 'Save standard values',
      onAccept: () => {
        setConfirmation(null);
        // The pair travels together, so a rejection always highlights rows from
        // the body that was actually sent — never from a plan rebuilt after it.
        void sendSave(attempt);
      },
    });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // No grid and no failure is the moment *before* the first response, not a
  // failed load. Without this the healthy first frame paints the failure card
  // at every admin who opens the page.
  const isAwaitingFirstGrid = !grid && !loadFailed;

  // Every editable control on the grid is held shut for the whole save — the
  // blast-radius count, the dialog, and the PUT. The invariant: nothing typed
  // during a save can be lost to that save's response, which replaces the grid
  // and drops every pending edit. It also keeps the dialog honest, since the
  // plan it describes cannot change between the question and the answer.
  //
  // Both terms are needed: `isSaving` drops before the dialog opens (its
  // `finally` belongs to the count, and to the PUT), so the dialog window is
  // carried by the second term. Any open confirmation freezes the grid — a
  // dialog is a decision about the state on screen, and that state must hold
  // still until the decision is made.
  const frozen = isSaving || confirmation !== null;

  // Everything that writes a value is held shut by the freeze *and* by the
  // absence of edit permission. `frozen` stays the narrower flag on purpose:
  // it also governs the tier buttons, the clinic picker and the discipline
  // switch, which only change which grid is on screen. Folding `!canEdit` into
  // it would leave a view-only admin at a multi-clinic agency stuck on the
  // first clinic's grid, unable to reach the other clinics or the organization
  // standard -- the one thing a view slug is for.
  const editsFrozen = frozen || !canEdit;

  // Give the keyboard its seat back, and only once the whole freeze is over.
  // Mid-dialog the dialog owns focus and this must not fight it; on release
  // there is nothing left holding it, so `document.body` is where a keyboard
  // manager would otherwise be standing. A control that has since gone away or
  // gone disabled — Save after a successful save, for one — is not somewhere to
  // put her, so the seat is simply let go.
  useEffect(() => {
    if (frozen) return;
    const target = freezeFocusRef.current;
    freezeFocusRef.current = null;
    if (!target || !target.isConnected) return;
    if ((target as Partial<HTMLButtonElement>).disabled === true) return;
    target.focus();
  }, [frozen]);

  // Every control that changes which grid is loaded is held shut while a save
  // is in flight, so the response can only ever land on the grid it was
  // written from.
  const tierButton = (value: Tier, label: string) => (
    <Button
      size='sm'
      variant={tier === value ? 'default' : 'outline'}
      aria-pressed={tier === value}
      disabled={frozen}
      onClick={() => guardSwitch(() => setTier(value))}
    >
      {label}
    </Button>
  );

  return (
    <>
      {/* min-w-0: this section is a child of Admin's `grid gap-6`, and grid items
          default to min-width:auto — without this, the table's column minimums
          widen the page column on narrow screens instead of engaging the
          overflow-x-auto wrapper around the table. */}
      <div
        data-testid='scheduling-points-section'
        className='min-w-0 space-y-6'
      >
        <div className='space-y-1.5'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div>
              <p className='text-sm text-muted-foreground'>
                How much work each activity represents. Values you leave alone
                are inherited, not blank.
              </p>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              {tierButton('clinic', 'This clinic')}
              {tierButton('agency', 'Organization standard')}
            </div>
          </div>

          {tier === 'clinic' && (
            <div className='mt-3 flex flex-wrap items-center gap-4'>
              <div className='min-w-[240px]'>
                <ClinicSelect
                  clinics={clinics}
                  value={clinicId}
                  disabled={frozen}
                  onChange={e => {
                    const next = e.target.value;
                    guardSwitch(() => setClinicId(next));
                  }}
                  placeholder='Select a clinic'
                />
              </div>
              <label className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Switch
                  checked={includeAllDisciplines}
                  aria-label='Show every discipline'
                  disabled={frozen}
                  onCheckedChange={checked =>
                    guardSwitch(() => setIncludeAllDisciplines(checked))
                  }
                />
                Show every discipline
              </label>
            </div>
          )}
        </div>

        {/* The freeze is visible as greyed controls; said out loud here so it
            is also audible. */}
        <div className='space-y-4' aria-busy={frozen || undefined}>
          {tier === 'clinic' && !clinicId ? (
            isLoadingClinics ? (
              <div className='flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground'>
                <Loader2 className='h-5 w-5 animate-spin' />
                Loading clinics…
              </div>
            ) : (
              <p className='py-16 text-center text-sm text-muted-foreground'>
                {clinics.length === 0
                  ? 'Create a clinic before setting scheduling points.'
                  : 'Select a clinic to see its scheduling points.'}
              </p>
            )
          ) : isLoading || isAwaitingFirstGrid ? (
            <div className='flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground'>
              <Loader2 className='h-5 w-5 animate-spin' />
              Loading scheduling points…
            </div>
          ) : loadFailed || !grid ? (
            <div className='flex flex-col items-center gap-3 py-16 text-sm text-muted-foreground'>
              <span>Scheduling points could not be loaded.</span>
              <Button variant='outline' onClick={() => void loadGrid()}>
                <RefreshCw className='mr-2 h-4 w-4' />
                Try again
              </Button>
            </div>
          ) : (
            <>
              {saveFailed && (
                <div
                  role='alert'
                  className='flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm'
                >
                  <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-destructive' />
                  <span>
                    <span className='font-medium'>
                      That didn&rsquo;t save — your changes are still here.
                    </span>{' '}
                    Nothing was discarded. Adjust anything you need to and save
                    again.
                  </span>
                </div>
              )}

              {/* Only shown when the unit is abstract. A clinic reading "hours"
                  has already made the number dimensional; explaining ratios to
                  it would be condescending. */}
              {grid.show_anchor_sentence && (
                <p className='text-sm text-muted-foreground'>
                  A routine nursing visit is 2.0. Everything else is relative to
                  it.
                </p>
              )}

              <p className='text-xs text-muted-foreground'>
                {provenanceCounts.own} of {rows.length} values{' '}
                {agree(provenanceCounts.own, 'is', 'are')}{' '}
                {tier === 'clinic'
                  ? "this clinic's own"
                  : "your organization's own"}
                {provenanceCounts.agency > 0 &&
                  ` · ${provenanceCounts.agency} from your organization's standard values`}
                {provenanceCounts.global > 0 &&
                  ` · ${provenanceCounts.global} from Roger's suggested values`}
                {provenanceCounts.unpriced > 0 &&
                  ` · ${provenanceCounts.unpriced} not yet priced`}
              </p>

              {/* Said where the provenance is read, because it is the same
                  question: the date beside a row says when a value started
                  applying, and a manager would otherwise take that to mean the
                  weeks before it keep their old numbers. They do not — every
                  week is priced at whatever the values say today. */}
              <p className='text-xs text-muted-foreground'>
                A saved value changes workload numbers for past weeks too.
              </p>

              <div className='flex flex-wrap items-end gap-6 rounded-md border bg-muted/20 px-3 py-3'>
                <div className='space-y-1'>
                  <label
                    className='text-sm font-medium'
                    htmlFor='scheduling-points-unit-label'
                  >
                    What you call a unit
                  </label>
                  <Input
                    id='scheduling-points-unit-label'
                    inputSize='sm'
                    className='w-48'
                    maxLength={32}
                    aria-label='What you call a unit'
                    disabled={editsFrozen}
                    value={unitLabel}
                    onChange={e =>
                      setConfigEdits(prev => ({
                        ...prev,
                        unitLabel: e.target.value,
                      }))
                    }
                  />
                  <p className='text-xs text-muted-foreground'>
                    {sourceLine(grid.unit_label_source, tier)}
                  </p>
                </div>

                <div className='space-y-1'>
                  <label
                    className='text-sm font-medium'
                    htmlFor='scheduling-points-travel'
                  >
                    Missed visit — travel
                  </label>
                  <Input
                    id='scheduling-points-travel'
                    inputSize='sm'
                    className={`w-28 ${travelInvalid ? 'border-destructive' : ''}`}
                    inputMode='decimal'
                    aria-label='Missed visit — travel'
                    aria-invalid={travelInvalid || undefined}
                    disabled={editsFrozen}
                    value={
                      configEdits.travel !== undefined
                        ? configEdits.travel
                        : formatPoints(grid.missed_visit_travel_points)
                    }
                    onChange={e => {
                      const next = e.target.value;
                      if (!POINTS_INPUT_RE.test(next)) return;
                      setConfigEdits(prev => ({ ...prev, travel: next }));
                    }}
                  />
                  {/* Said in the line that would otherwise carry provenance,
                      so the field neither reflows nor stays silent about why
                      the save is refusing to go. */}
                  {travelInvalid ? (
                    <p className='text-xs text-destructive'>
                      {POINTS_RANGE_SENTENCE}
                    </p>
                  ) : (
                    <p className='text-xs text-muted-foreground'>
                      {sourceLine(grid.missed_visit_travel_source, tier)}
                    </p>
                  )}
                </div>
              </div>

              {/* Stated, not silent: the exclusion would otherwise read as an
                  oversight to anyone who sees uploads on a task list. */}
              <p className='text-xs text-muted-foreground'>
                {grid.excluded_note}
              </p>

              <Accordion
                type='multiple'
                value={openCategories}
                onValueChange={setOpenCategories}
              >
                {categories.map(category => {
                  const own = category.rows.filter(
                    row => row.source === tier
                  ).length;
                  const pending = category.rows.reduce(
                    (total, row) =>
                      total + (plan.changedByRow.get(rowKey(row))?.length ?? 0),
                    0
                  );
                  return (
                    <AccordionItem
                      key={category.category}
                      value={category.category}
                    >
                      <AccordionTrigger>
                        <span className='flex items-center gap-2'>
                          <span className='font-medium'>{category.label}</span>
                          <span className='text-xs font-normal text-muted-foreground'>
                            {own} of {category.rows.length} set
                          </span>
                          {pending > 0 && (
                            <Badge variant='secondary'>{pending} pending</Badge>
                          )}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className='space-y-6'>
                          {category.groups.map(group => (
                            <PointsGroup
                              key={group.groupKey}
                              group={group}
                              categoryLabel={category.label}
                              tier={tier}
                              edits={edits}
                              changedByRow={plan.changedByRow}
                              invalidRowKeys={invalidRowKeys}
                              saveFailed={saveFailed}
                              frozen={editsFrozen}
                              valueColumnLabel={valueColumnLabel}
                              bulkValue={bulkText[group.groupKey] ?? ''}
                              onBulkValueChange={value =>
                                setBulkText(prev => ({
                                  ...prev,
                                  [group.groupKey]: value,
                                }))
                              }
                              onBulkSet={() =>
                                requestBulkSet(group, category.label)
                              }
                              onGroupReset={() =>
                                requestGroupReset(group, category.label)
                              }
                              onUpdateRow={updateRow}
                            />
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              {/* Stacked on a phone, unchanged on a desktop: two no-shrink
                  buttons and a status line do not fit on one row at 390px. */}
              <div className='flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-end'>
                <span className='text-xs text-muted-foreground sm:mr-auto'>
                  {invalidCount > 0
                    ? `Fix the highlighted ${agree(invalidCount, 'value', 'values')} to save.`
                    : pendingCount > 0
                      ? `${plural(pendingCount, 'unsaved change', 'unsaved changes')}`
                      : 'No unsaved changes'}
                </span>
                <Button
                  variant='outline'
                  onClick={requestDiscardEdits}
                  disabled={!isDirty || editsFrozen}
                >
                  Discard changes
                </Button>
                <Button
                  onClick={() => void handleSave()}
                  disabled={pendingCount === 0 || editsFrozen}
                >
                  {isSaving && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Save scheduling points
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={confirmation !== null}
        title={confirmation?.title ?? ''}
        message={confirmation?.message ?? ''}
        confirmLabel={confirmation?.confirmLabel ?? 'Confirm'}
        cancelLabel='Cancel'
        onAccept={() => confirmation?.onAccept()}
        onCancel={() => setConfirmation(null)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// One discipline's rows inside a category
// ---------------------------------------------------------------------------

type PointsGroupProps = {
  group: DisciplineGroup;
  categoryLabel: string;
  tier: Tier;
  edits: EditMap;
  changedByRow: Map<string, ChangedField[]>;
  invalidRowKeys: string[];
  saveFailed: boolean;
  /** Held shut for the length of a save; see the invariant where it is set. */
  frozen: boolean;
  valueColumnLabel: string;
  bulkValue: string;
  onBulkValueChange: (value: string) => void;
  onBulkSet: () => void;
  onGroupReset: () => void;
  onUpdateRow: (key: string, patch: RowEdits) => void;
};

const PointsGroup: React.FC<PointsGroupProps> = ({
  group,
  categoryLabel,
  tier,
  edits,
  changedByRow,
  invalidRowKeys,
  saveFailed,
  frozen,
  valueColumnLabel,
  bulkValue,
  onBulkValueChange,
  onBulkSet,
  onGroupReset,
  onUpdateRow,
}) => {
  const groupLabel = groupDisplayName(group, categoryLabel);
  const ownCount = group.rows.filter(row => row.source === tier).length;
  // The count in the heading is about the saved grid; whether resetting can do
  // anything is about the screen. A group whose only values were typed a moment
  // ago has something to reset, and an offer that refused it would be wrong.
  // A Counted switch counts as something to reset for the same reason.
  const canReset = group.rows.some(row => {
    const stated = stateRow(row, edits[rowKey(row)], tier);
    return stated.isOwn || stated.ownsEnabled;
  });

  return (
    <div className='space-y-2'>
      <div className='flex flex-wrap items-center gap-2'>
        <h4 className='text-sm font-medium'>{group.disciplineLabel}</h4>
        <span className='text-xs text-muted-foreground'>
          {ownCount} of {group.rows.length} set
        </span>
        <div className='ml-auto flex items-center gap-2'>
          <Input
            inputSize='sm'
            className='w-20'
            inputMode='decimal'
            placeholder='2'
            aria-label={`Set all ${groupLabel} to`}
            disabled={frozen}
            value={bulkValue}
            onChange={e => {
              if (!POINTS_INPUT_RE.test(e.target.value)) return;
              onBulkValueChange(e.target.value);
            }}
          />
          {/* Every group repeats these two words, so the accessible name has
              to carry the group; five identical "Set all" buttons in a row is
              a real problem for anyone not looking at the screen. */}
          <Button
            size='sm'
            variant='outline'
            aria-label={`Set all ${groupLabel}`}
            disabled={frozen}
            onClick={onBulkSet}
          >
            Set all
          </Button>
          {/* The explanation hangs on the wrapper, not the button: a disabled
              button takes no pointer events, so a title on it never shows. */}
          <span
            className='inline-flex'
            title={
              canReset
                ? undefined
                : `${TIER_POSSESSIVE_SENTENCE_START[tier]} has no values of its own here.`
            }
          >
            <Button
              size='sm'
              variant='ghost'
              aria-label={`Reset ${groupLabel} to suggested`}
              onClick={onGroupReset}
              disabled={!canReset || frozen}
            >
              Reset to suggested
            </Button>
          </span>
        </div>
      </div>

      <div className='overflow-x-auto'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='min-w-[220px]'>Activity</TableHead>
              <TableHead className='w-28'>{valueColumnLabel}</TableHead>
              <TableHead className='min-w-[200px]'>
                Where it comes from
              </TableHead>
              <TableHead className='w-24'>Counted</TableHead>
              <TableHead className='min-w-[240px]'>
                What did you compare this to?
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.rows.map(row => {
              const key = rowKey(row);
              return (
                <PointsRow
                  key={key}
                  row={row}
                  tier={tier}
                  edit={edits[key]}
                  changedFields={changedByRow.get(key) ?? []}
                  isInvalid={invalidRowKeys.includes(key)}
                  saveFailed={saveFailed}
                  frozen={frozen}
                  valueColumnLabel={valueColumnLabel}
                  onUpdate={patch => onUpdateRow(key, patch)}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// One row
// ---------------------------------------------------------------------------

type PointsRowProps = {
  row: SchedulingPointRowResponse;
  tier: Tier;
  edit: RowEdits | undefined;
  changedFields: readonly ChangedField[];
  isInvalid: boolean;
  saveFailed: boolean;
  /** Held shut for the length of a save; see the invariant where it is set. */
  frozen: boolean;
  /** The column's own heading, so the cell's accessible name matches it. */
  valueColumnLabel: string;
  onUpdate: (patch: RowEdits) => void;
};

const PointsRow: React.FC<PointsRowProps> = ({
  row,
  tier,
  edit,
  changedFields,
  isInvalid,
  saveFailed,
  frozen,
  valueColumnLabel,
  onUpdate,
}) => {
  const isOwn = row.source === tier;
  const state = pointsState(row);
  const pointsText = edit?.points ?? formatPoints(row.points);
  // `??` would read a pending reset as "nothing typed" and show the value being
  // reset away, so `null` is handled before it: a queued reset already reads as
  // where the switch is about to land.
  const enabled =
    edit?.enabled === null
      ? row.inherited_enabled
      : (edit?.enabled ?? row.enabled);
  // Offered only where this tier stated the choice and has not already queued
  // something else — once an edit is pending, the switch itself is the way back.
  const canUseDefault =
    edit?.enabled === undefined && row.enabled_source === tier;
  const noteText = edit?.note ?? row.note ?? '';

  const pointsChanged = changedFields.includes('points');
  const noteChanged = changedFields.includes('note');
  const enabledChanged = changedFields.includes('enabled');

  // A pending edit reads as pending; after a failed save the same cells go
  // grey, which says "held, not written" without taking them away.
  const pendingClass = (changed: boolean) =>
    changed
      ? saveFailed
        ? 'border-muted-foreground/50 bg-muted opacity-70'
        : 'border-primary bg-primary/5'
      : '';

  // A value stated at this tier is hers and reads prominently. Everything
  // else is recessive — present and correct, but visibly not her handwriting.
  const inheritedClass = isOwn
    ? 'font-medium'
    : 'text-muted-foreground bg-muted/40';

  return (
    <TableRow data-row-key={rowKey(row)}>
      <TableCell className={enabled ? '' : 'text-muted-foreground'}>
        {row.display_name}
      </TableCell>

      <TableCell>
        <div className='flex items-center gap-1.5'>
          <Input
            inputSize='sm'
            className={`w-20 text-right ${inheritedClass} ${pendingClass(pointsChanged)} ${
              isInvalid ? 'border-destructive' : ''
            }`}
            inputMode='decimal'
            // Absence renders as an em dash and never as a number, so a row
            // nobody has priced can never be mistaken for one priced at zero.
            placeholder='—'
            // A clinic that renamed the unit says "hours" in its column
            // heading; the cell a screen reader announces has to say the same.
            aria-label={`${row.display_name} ${valueColumnLabel}`}
            aria-invalid={isInvalid || undefined}
            data-points-state={state}
            data-points-source={row.source}
            data-pending={pointsChanged || undefined}
            data-save-failed={(pointsChanged && saveFailed) || undefined}
            disabled={frozen}
            value={pointsText}
            onChange={e => {
              if (!POINTS_INPUT_RE.test(e.target.value)) return;
              onUpdate({ points: e.target.value });
            }}
          />
          {state === 'zero' && edit?.points === undefined && (
            <span className='whitespace-nowrap text-xs text-muted-foreground'>
              counted at zero
            </span>
          )}
        </div>
      </TableCell>

      <TableCell>
        <span
          className={`text-xs ${isOwn ? 'text-foreground' : 'text-muted-foreground'}`}
          data-source={row.source}
        >
          {sourceLine(row.source, tier)}
          {isOwn && row.effective_from
            ? ` · ${formatDateForDisplay(row.effective_from)}`
            : ''}
        </span>
      </TableCell>

      <TableCell>
        <div className='flex items-center gap-1'>
          <Switch
            checked={enabled}
            aria-label={`Count ${row.display_name}`}
            data-pending={enabledChanged || undefined}
            disabled={frozen}
            onCheckedChange={checked => onUpdate({ enabled: checked })}
          />
          {canUseDefault && (
            <Button
              size='xs'
              variant='ghost'
              aria-label={`Use the default for ${row.display_name}`}
              disabled={frozen}
              onClick={() => onUpdate({ enabled: null })}
            >
              Use default
            </Button>
          )}
        </div>
      </TableCell>

      <TableCell>
        <Input
          inputSize='sm'
          className={`w-full ${pendingClass(noteChanged)}`}
          maxLength={280}
          aria-label={`What did you compare this to? — ${row.display_name}`}
          data-pending={noteChanged || undefined}
          disabled={frozen}
          value={noteText}
          onChange={e => onUpdate({ note: e.target.value })}
        />
      </TableCell>
    </TableRow>
  );
};

export default SchedulingPointsSection;
